import React, { useRef } from "react";
import { Bold, Italic, Link2, Eraser } from "lucide-react";

function isLong(value) {
  return typeof value === "string" && (value.length > 80 || value.includes("\n"));
}

function wrapSelection(textarea, before, after = before) {
  const start = textarea.selectionStart ?? 0;
  const end = textarea.selectionEnd ?? 0;
  const value = textarea.value;
  const selected = value.slice(start, end) || "text";
  const next = value.slice(0, start) + before + selected + after + value.slice(end);
  return { next, cursorStart: start + before.length, cursorEnd: start + before.length + selected.length };
}

export default function StringField({ value, onChange, path, isLongHint }) {
  const long = isLongHint || isLong(value);
  const tid = `field-${path.join(".")}`;
  const ref = useRef(null);
  const cls = "w-full bg-[#080808] border border-white/15 text-[#f5f5f5] p-3 font-mono text-xs focus:outline-none focus:border-white/40";

  if (!long) {
    return (
      <input
        data-testid={tid}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className={cls}
      />
    );
  }

  const apply = (before, after = before) => {
    const t = ref.current;
    if (!t) return;
    const { next, cursorStart, cursorEnd } = wrapSelection(t, before, after);
    onChange(next);
    requestAnimationFrame(() => {
      try {
        t.focus();
        t.setSelectionRange(cursorStart, cursorEnd);
      } catch { /* noop */ }
    });
  };

  const insertLink = () => {
    const t = ref.current;
    if (!t) return;
    const url = window.prompt("Link URL:", "https://");
    if (!url) return;
    const start = t.selectionStart ?? 0;
    const end = t.selectionEnd ?? 0;
    const sel = t.value.slice(start, end) || "link text";
    const html = `<a href="${url}" target="_blank" rel="noreferrer">${sel}</a>`;
    const next = t.value.slice(0, start) + html + t.value.slice(end);
    onChange(next);
  };

  const stripHtml = () => {
    const stripped = (value || "").replace(/<[^>]+>/g, "");
    onChange(stripped);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-1" data-testid={`rich-toolbar-${path.join(".")}`}>
        <ToolbarBtn label="Bold" testId={`rich-bold-${path.join(".")}`} onClick={() => apply("<strong>", "</strong>")}>
          <Bold size={12} />
        </ToolbarBtn>
        <ToolbarBtn label="Italic" testId={`rich-italic-${path.join(".")}`} onClick={() => apply("<em>", "</em>")}>
          <Italic size={12} />
        </ToolbarBtn>
        <ToolbarBtn label="Link" testId={`rich-link-${path.join(".")}`} onClick={insertLink}>
          <Link2 size={12} />
        </ToolbarBtn>
        <ToolbarBtn label="Strip HTML" testId={`rich-strip-${path.join(".")}`} onClick={stripHtml}>
          <Eraser size={12} />
        </ToolbarBtn>
        <span className="ml-2 font-mono text-[9px] tracking-[0.25em] uppercase text-[#a3a3a3]">
          rich · &lt;strong&gt; &lt;em&gt; &lt;a&gt;
        </span>
      </div>
      <textarea
        ref={ref}
        data-testid={tid}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        rows={Math.min(8, Math.max(3, (value || "").split("\n").length + 1))}
        className={cls}
      />
    </div>
  );
}

function ToolbarBtn({ children, onClick, label, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      data-testid={testId}
      className="font-mono text-[10px] tracking-[0.25em] uppercase border border-white/15 text-[#a3a3a3] hover:text-white hover:border-white/40 px-2 py-1.5 transition-all"
    >
      {children}
    </button>
  );
}
